import {
  Facebook,
  Instagram,
  LinkedIn,
  Pinterest,
  Public,
  Reddit,
  Twitter,
  YouTube,
} from '@mui/icons-material';
import {
  CircularProgress,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { mediaFromIframely, OneMedia } from '@prismamedia/one-components';
import type { FC, KeyboardEvent } from 'react';
import React, { useCallback, useState } from 'react';
import type { IframelyFn } from '../MultimediaPlugin/types';
import {
  CERISE_INVALID_URL_ERROR_MESSAGE,
  MEDIA_TEXT_FIELD_PLACEHOLDER,
} from './constants';
import { useStyles } from './styles';

interface EmbedDialogProps {
  title?: string;
  fetcher: IframelyFn;
  forbiddenURLMediaList?: string[];
  onMediaLoaded: { (media: OneMedia): void };
  validateInputUrl?: (url: string) => void;
  showFooterIcons?: boolean;
}

export const EmbedDialog: FC<EmbedDialogProps> = ({
  title = 'Média externe',
  validateInputUrl,
  showFooterIcons = true,
  fetcher,
  forbiddenURLMediaList = [],
  onMediaLoaded,
}) => {
  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [value, setValue] = useState<string>('');

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setValue(event.target.value);
    },
    [],
  );

  const handleKeyUp = useCallback(
    async (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();

      const isURLMediaAllowed = !forbiddenURLMediaList.find((mediaURL) =>
        value.includes(mediaURL),
      );

      if (!isURLMediaAllowed) {
        setError(CERISE_INVALID_URL_ERROR_MESSAGE);
        return;
      }

      setLoading(true);
      setError('');

      try {
        // use user custom validation function if any provided
        validateInputUrl?.(value);

        const media = await fetcher(value);
        onMediaLoaded(mediaFromIframely(media));
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        }
      }

      setLoading(false);
    },
    [fetcher, forbiddenURLMediaList, onMediaLoaded, value, validateInputUrl],
  );

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className={classes.content}>
        <TextField
          autoFocus
          disabled={loading}
          fullWidth
          onChange={handleInputChange}
          onKeyUp={handleKeyUp}
          placeholder={MEDIA_TEXT_FIELD_PLACEHOLDER}
          value={value}
          variant="standard"
        />
        {error && <DialogContentText color="error">{error}</DialogContentText>}
        {showFooterIcons && (
          <DialogContentText className={classes.medias}>
            <YouTube className={classes.mediaIcon} />
            <Facebook className={classes.mediaIcon} />
            <Instagram className={classes.mediaIcon} />
            <Twitter className={classes.mediaIcon} />
            <LinkedIn className={classes.mediaIcon} />
            <Pinterest className={classes.mediaIcon} />
            <Reddit className={classes.mediaIcon} />
            <Public className={classes.mediaIcon} />
          </DialogContentText>
        )}
        {loading && <CircularProgress className={classes.loader} />}
      </DialogContent>
    </>
  );
};
